import React from 'react'
import {Stack, Text, Box, Flex} from '../../vanilla'
import {MastercardAltIcon, VisaAltIcon} from '../icons'
import {
  braintreeInputStyle,
  braintreeInputStyleValid,
  braintreeInputStyleInvalid,
  braintreeInputStylePotentiallyValid,
} from './styles.css'
import { BraintreeHostedFieldsProps, BraintreeLabelProps, FieldProps } from '../../types/braintree'

export const BraintreeLabel = ({labelText, icons = false}: BraintreeLabelProps) => {
  return (
    <Flex>
      <Text
        variant="unstyled"
        fontSize="sm"
        lineHeight="none"
        style={{
          color: 'red',
          width: 'fit-content',
          position: 'relative',
          top: '15px',
          left: '5px',
          background: 'linear-gradient(#fff,#fff 25%,#fafafa)',
          paddingLeft: '4px',
          paddingRight: '8px',
        }}
      >
        {' '}
        •{' '}
      </Text>
      <Text
        variant="unstyled"
        fontSize="sm"
        lineHeight="none"
        style={{
          width: 'fit-content',
          position: 'relative',
          top: '15px',
          background: 'linear-gradient(#fff,#fff 25%,#fafafa)',
          paddingRight: '10px',
        }}
      >
        {labelText}
      </Text>
      {icons && (
        <Stack
          direction="row"
          style={{
            width: 'fit-content',
            position: 'relative',
            top: '15px',
            background: 'linear-gradient(#fff,#fff 25%,#fafafa)',
            paddingRight: '10px',
          }}
        >
          <VisaAltIcon width="20px" height="auto" />
          <MastercardAltIcon width="20px" height="auto" />
        </Stack>
      )}
    </Flex>
  )
}

export const getBraintreeInputClassName = ({isPotentiallyValid, isValid, isEmpty}: FieldProps) => {
    
  if (!isValid && !isEmpty) {
    return `${braintreeInputStyle} ${braintreeInputStyleInvalid}`
  }
  
  if (isValid) return `${braintreeInputStyle} ${braintreeInputStyleValid}`
  
  if (!isPotentiallyValid) return `${braintreeInputStyle} ${braintreeInputStylePotentiallyValid}`
  
  return braintreeInputStyle
}

export const BraintreeHostedFields = ({
  usingSavedCard,
  braintreeInputs,
  validationError,
  inputRefs
}: BraintreeHostedFieldsProps & { inputRefs: {[key: string]: HTMLInputElement | null}}) => {
  const {number, cvv, expirationDate, cardholderName} = braintreeInputs

  const validationFieldError =
    validationError.cardholderName ||
    validationError.number ||
    validationError.expirationDate ||
    validationError.cvv
  return (
    <Stack bg="white">
      {validationFieldError && (
        <Box paddingY="16px" paddingX={['20px', '28px']} style={{backgroundColor: '#fdf7f8'}}>
          <Text variant="text3" color="error">
            {validationError.number
              ? 'Card number not accepted. Please try again using a Visa or Mastercard.'
              : 'Some payment input fields are invalid.'}
          </Text>
        </Box>
      )}
      <Stack direction={['column']} spacing={['4px', '16px']} width={['full', 'half']}>
        <Stack style={{display: usingSavedCard ? 'none' : 'flex'}}>
          <BraintreeLabel labelText="Name on Card" />
          <Box
            ref={(el: HTMLInputElement) => (inputRefs.cardName = el)}
            className={
              cardholderName ? getBraintreeInputClassName(cardholderName as FieldProps) : braintreeInputStyle
            }
            id="card-holder-name"
            data-test-selector="card-holder-name-input"
          />
        </Stack>
        <Stack style={{display: usingSavedCard ? 'none' : 'flex'}}>
          <BraintreeLabel labelText="Card Number" icons={true} />
          <Box
            ref={(el: HTMLInputElement) => (inputRefs.cardNumber = el)}
            className={number ? getBraintreeInputClassName(number as FieldProps) : braintreeInputStyle}
            id="card-number"
            data-test-selector="card-number-input"
          />
          <Text>Example: 4111111111111111</Text>
        </Stack>
      </Stack>

      <Stack direction="row" spacing="16px" width={['full', 'half']} marginBottom='16px'>
        <Stack style={{display: usingSavedCard ? 'none' : 'flex'}} width='half'>
          <BraintreeLabel labelText="Expiry Date" />
          <Box
            ref={(el: HTMLInputElement) => (inputRefs.cardExpiry = el)}
            className={
              expirationDate ? getBraintreeInputClassName(expirationDate as FieldProps) : braintreeInputStyle
            }
            id="expiration-date"
            data-test-selector="expiry-date-input"
          />
          <Text style={{display: usingSavedCard ? 'none' : 'flex'}}>Example: MM/YY</Text>
        </Stack>
        <Stack style={{display: usingSavedCard ? 'none' : 'flex'}} width='half'>
          <BraintreeLabel labelText="CV Number" />
          <Box
            ref={(el: HTMLInputElement) => (inputRefs.cardCvv = el)}
            className={cvv ? getBraintreeInputClassName(cvv as FieldProps) : braintreeInputStyle}
            id="cvv"
            data-test-selector="cv-number-input"
          />
          <Text lineHeight='none'>Last 3 digits on the back of your card</Text>
        </Stack>
      </Stack>
    </Stack>
  )
}